/** The frame every screen shares: shell, loading and error states, and the drawer.
 *
 * Kept in one place so a screen is only its panels. The drawer is rendered here because its state
 * is in the URL, which every screen reads the same way.
 */

import type { ReactNode } from "react";

import type { Figure } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { useSkeletonHold } from "../lib/useSkeletonHold";
import type { BreakdownPaging, DrawerState } from "../components/ReceiptDrawer";
import { ReceiptDrawer } from "../components/ReceiptDrawer";
import { AppShell } from "../shell/AppShell";
import type { RecordPlace } from "../shell/routeTable";
import styles from "./ScreenFrame.module.css";
import type { Screen } from "./useScreen";

interface Props {
  screen: Screen;
  title: string;
  crumb?: string;
  /** Figures the drawer lists when a breakdown is open. The screen decides what a given
   * breakdown key means, because only it knows which figures its KPI summed. */
  breakdownFigures?: readonly Figure[];
  breakdownLabel?: string;
  /** When the figures are **a page of a larger selection** (`useFiguresPaged`): how many exist, and
   * how to read more. The drawer then says *Showing N of M* and offers the rest. */
  breakdownPaging?: BreakdownPaging;
  /** The server's total, in kilograms, for exactly the selection the drawer lists. */
  breakdownHeadline?: string;
  /** A sentence the screen owes the reader about the selection, printed under the list. */
  breakdownNote?: string;
  /** For a screen whose KPIs are the server's sums, the **on-demand read** of the figures behind a
   * breakdown. While it is pending or has failed the drawer says so, rather than listing nothing
   * (which is a claim that nothing composes the total). */
  breakdownRead?: { isPending: boolean; error: unknown; refetch: () => void };
  /** Passed through to the shell. See `AppShell`'s own prop. */
  bodyScrolls?: boolean;
  /** The latest **issued filing** (artifact id) for the filing this screen is about, so a receipt
   * opened here says which rules version that filing used (UX-02). Absent when nothing has been
   * issued, and then the receipt claims no filing. */
  receiptFiling?: string;
  /** The place this page belongs to, when its path serves many (a record). See `recordPlace`. */
  lights?: RecordPlace;
  children: ReactNode;
}

/** What stands in for a screen while its workspace is being read: the shape of a data place - the header
 * card, a strip of five tiles, and the table card with its filter line and eight rows - drawn as bones
 * at the heights the loaded page has (33-ui-build-spec, States: a skeleton shaped like the result,
 * never a spinner). "Loading…" stays as real, visually hidden text: it is what assistive technology
 * announces and what the screen-state tests look for to prove a screen is not stuck. */
function WorkspaceSkeleton() {
  return (
    <div className={styles.loading} role="status" aria-busy="true">
      <span className="visuallyHidden">Loading…</span>
      <div className={`card ${styles.head}`} aria-hidden="true">
        <span className={`skeleton ${styles.badge}`} />
        <span className={`skeleton ${styles.headTitle}`} />
      </div>
      <div className={styles.strip} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((cell) => (
          <div key={cell} className={`card ${styles.tile}`}>
            <div className={styles.tileLabel}>
              <span className={`skeleton ${styles.label}`} />
            </div>
            <div className={styles.tileFigure}>
              <span className={`skeleton ${styles.figure}`} />
            </div>
          </div>
        ))}
      </div>
      <div className={`card ${styles.tableCard}`} aria-hidden="true">
        <div className={styles.filterLine}>
          <span className={`skeleton ${styles.filter}`} />
        </div>
        {Array.from({ length: 8 }, (_, row) => (
          <div key={row} className={styles.row}>
            <span className={`skeleton ${styles.line}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScreenFrame({
  screen,
  title,
  crumb,
  breakdownFigures,
  breakdownLabel,
  breakdownPaging,
  breakdownHeadline,
  breakdownNote,
  breakdownRead,
  bodyScrolls,
  receiptFiling,
  lights,
  children,
}: Props) {
  const showSkeleton = useSkeletonHold(screen.isLoading);
  let drawer: DrawerState | null = null;
  if (screen.receipt) {
    drawer = {
      kind: "receipt",
      calculationId: screen.receipt,
      ...(screen.receiptPart ? { part: screen.receiptPart } : {}),
      backLabel: screen.breakdown && breakdownLabel ? breakdownLabel : undefined,
      ...(receiptFiling ? { filingArtifactId: receiptFiling } : {}),
    };
  } else if (
    screen.breakdown &&
    breakdownRead &&
    (breakdownRead.isPending || Boolean(breakdownRead.error))
  ) {
    drawer = {
      kind: "breakdown_unread",
      label: breakdownLabel ?? "Breakdown",
      error: breakdownRead.error,
      onRetry: () => breakdownRead.refetch(),
    };
  } else if (screen.breakdown && breakdownFigures) {
    drawer = {
      kind: "breakdown",
      label: breakdownLabel ?? "Breakdown",
      figures: breakdownFigures,
      ...(breakdownPaging ? { paging: breakdownPaging } : {}),
      ...(breakdownHeadline !== undefined ? { headline: breakdownHeadline } : {}),
      ...(breakdownNote ? { note: breakdownNote } : {}),
    };
  }

  return (
    <AppShell
      title={title}
      crumb={crumb}
      memberships={screen.memberships}
      membershipsFailed={
        screen.membershipsError
          ? { error: screen.membershipsError, onRetry: screen.retryMemberships }
          : null
      }
      organization={screen.organization}
      onOrganizationChange={screen.setOrganization}
      method={screen.method}
      onMethodChange={screen.setMethod}
      period={screen.window}
      periods={screen.windows}
      onPeriodChange={screen.setWindow}
      bodyScrolls={bodyScrolls}
      lights={lights}
    >
      {screen.error ? (
        <div className={styles.state}>
          {/* **The shared read-failure treatment, not a sentence of its own** — and not a developer's
              instruction. This used to say *"The API may not be running. Try `make dev`, and
              `make seed` if there are no workspaces"* to whoever was signed in, which is advice for
              the person who built the product printed at the person who bought it. `ReadFailed` is
              the one rendering of "a read failed" (role=alert, the failure's own sentence, a way to
              ask again), so this screen can no longer describe the same failure differently from
              the thirty that use it. */}
          <div className={styles.failed}>
            <ReadFailed of="Your workspace" error={screen.error} onRetry={screen.retry} />
          </div>
        </div>
      ) : showSkeleton ? (
        <WorkspaceSkeleton />
      ) : !screen.organization ? (
        <div className={styles.state}>
          <p className={styles.none} data-testid="no-workspace">
            No workspace is available to this account. If you were expecting one, ask the person
            who invited you.
          </p>
        </div>
      ) : (
        <>
          {children}
          {drawer ? (
            <ReceiptDrawer
              state={drawer}
              method={screen.method}
              onClose={screen.closeDrawer}
              onSelect={screen.openReceipt}
              onBack={
                screen.breakdown ? () => screen.openBreakdown(screen.breakdown as string) : undefined
              }
            />
          ) : null}
        </>
      )}
    </AppShell>
  );
}
